import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GTop100Logo from '../assets/GTop100Logo';
import api from '../utils/api';
import './Dashboard.css';

const NX_REWARD_PER_VOTE = 2000;

const Dashboard = () => {
  const [userInfo, setUserInfo] = useState({
    username: localStorage.getItem('username') || 'User',
    meso: 0,
    characters: [],
    banned: false,
    banReason: '',
    votePoints: 0,
    nxCredit: 0,
    maplePoint: 0,
    nxPrepaid: 0,
    todayVotes: 0,
    canVoteToday: false
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [voteLoading, setVoteLoading] = useState(false);
  const [voteMessage, setVoteMessage] = useState(null);
  const navigate = useNavigate();
  
  const fetchUserData = async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/auth/user');
      
      setUserInfo({
        id: res.data.id,
        username: res.data.username,
        email: res.data.email,
        banned: res.data.banned,
        banReason: res.data.banReason,
        nxCredit: res.data.nxCredit,
        maplePoint: res.data.maplePoint,
        nxPrepaid: res.data.nxPrepaid,
        votePoints: res.data.votePoints,
        totalMeso: res.data.totalMeso,
        characters: res.data.characters || [],
        todayVotes: 0,
        canVoteToday: true
      });
      
      await fetchVotingStatus();
      
    } catch (err) {
      console.error('Error fetching user data:', err);
      if (err.response && err.response.status === 401) {
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchVotingStatus = async () => {
    try {
      const res = await api.get('/voting/status');
      
      setUserInfo(prevState => ({
        ...prevState,
        votePoints: res.data.votePoints,
        todayVotes: res.data.todayVotes,
        canVoteToday: res.data.canVoteToday
      }));
      
    } catch (err) {
      console.error('Error fetching voting status:', err);
    }
  };
  
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    const token = localStorage.getItem('token');
    
    if (!isAuthenticated || !token) {
      navigate('/login');
      return;
    }
    
    fetchUserData();
  }, [navigate]);
  
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('username');
    navigate('/login');
  };
  
  const handleVote = async () => {
    setVoteLoading(true);
    setVoteMessage(null);
    
    try {
      // Check if user is logged into the game
      const loginCheckRes = await api.get(`/voting/check-login-status?username=${userInfo.username}`);
      
      if (loginCheckRes.data.isLoggedInGame) {
        setVoteMessage({
          type: 'error',
          text: 'You cannot vote while logged into the game. Please log out first!'
        });
        setVoteLoading(false);
        return;
      }
      
      // Open GTop100 in a new tab
      window.open(`https://gtop100.com/MapleStory/Kuro-a-revolutionary-MapleStory-private-server-entering-its-Alpha-stage-soon-104650?vote=1&pingUsername=${userInfo.id}`, '_blank');
      
      setVoteMessage({
        type: 'info',
        text: 'Vote page opened. Complete your vote, then wait for verification...'
      });
      
      // Start polling for vote verification
      let attempts = 0;
      const maxAttempts = 24; // 2 minutes (12 * 10 seconds)
      
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          // Verify vote
          const res = await api.post('/voting/verify');
          
          // Stop polling on success
          clearInterval(pollInterval);
          
          // Update vote data
          setUserInfo(prevState => ({
            ...prevState,
            votePoints: res.data.votePoints,
            todayVotes: res.data.todayVotes,
            canVoteToday: res.data.todayVotes < 3,
            nxCredit: res.data.nxCredit
          }));
          
          // Set success message
          setVoteMessage({
            type: 'success',
            text: res.data.msg
          });
          
        } catch (err) {
          if (attempts >= maxAttempts) {
            // Stop polling after max attempts
            clearInterval(pollInterval);
            
            if (err.response && err.response.data) {
              setVoteMessage({
                type: 'error',
                text: err.response.data.msg
              });
            } else {
              setVoteMessage({
                type: 'error',
                text: 'Verification timed out. Please try again later.'
              });
            }
          }
        }
      }, 5000); // Poll every 5 seconds
      
      // Cleanup polling when finished
      setTimeout(() => {
        clearInterval(pollInterval);
        setVoteLoading(false);
      }, 120000); // Stop after 2 minutes max
      
    } catch (err) {
      setVoteMessage({
        type: 'error',
        text: err.response?.data?.msg || 'Error checking account status'
      });
    } finally {
      setVoteLoading(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading account information...</p>
      </div>
    );
  }
  
  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="dashboard-title">
          <h1>Account Dashboard</h1>
          <p className="welcome-text">Welcome back, <span className="username-highlight">{userInfo.username}</span></p>
        </div>
        <div className="dashboard-actions">
          <button className="logout-button" onClick={handleLogout}>Logout</button>
        </div>
      </div>
      
      {userInfo.banned && (
        <div className="ban-notice">
          <h3>Account Banned</h3>
          <p>Reason: {userInfo.banReason || 'Violation of server rules'}</p>
          <p>Please contact an administrator on Discord for assistance.</p>
        </div>
      )}
      
      <div className="dashboard-main">
        <div className="dashboard-left">
          <div className="dashboard-section account-info">
            <h2>Account Information</h2>
            <div className="info-cards">
              <div className="info-card">
                <div className="info-icon">
                  <i className="fas fa-coins"></i>
                </div>
                <div className="info-content">
                  <span className="info-value">{userInfo.nxCredit.toLocaleString()}</span>
                  <span className="info-label">NX Credit</span>
                </div>
              </div>
              
              <div className="info-card">
                <div className="info-icon">
                  <i className="fas fa-star"></i>
                </div>
                <div className="info-content">
                  <span className="info-value">{userInfo.votePoints}</span>
                  <span className="info-label">Vote Points</span>
                </div>
              </div>
              
              <div className="info-card">
                <div className="info-icon">
                  <i className="fas fa-gem"></i>
                </div>
                <div className="info-content">
                  <span className="info-value">{userInfo.maplePoint?.toLocaleString() || 0}</span>
                  <span className="info-label">Maple Points</span>
                </div>
              </div>
              
              <div className="info-card">
                <div className="info-icon">
                  <i className="fas fa-sack-dollar"></i>
                </div>
                <div className="info-content">
                  <span className="info-value">{userInfo.totalMeso?.toLocaleString() || '0'}</span>
                  <span className="info-label">Total Meso</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="dashboard-section characters">
            <div className="section-header">
              <h2>Characters</h2>
              <span className="char-count">{userInfo.characters.length} / {userInfo.characterSlots || 3}</span>
            </div>
            
            {userInfo.characters.length === 0 ? (
              <div className="no-characters">
                <p>No characters found on this account.</p>
                <button className="create-character-btn">Create Character</button>
              </div>
            ) : (
              <div className="character-grid">
                {userInfo.characters.map(character => (
                  <div key={character.id} className="character-card">
                    <div className="character-header">
                      <h3 className="character-name">{character.name}</h3>
                      <span className="character-level">Lv. {character.level}</span>
                    </div>
                    <div className="character-job">{character.job}</div>
                    <div className="character-details">
                      <div className="detail-row">
                        <span className="detail-label">Meso:</span>
                        <span className="detail-value">{character.meso?.toLocaleString() || '0'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">STR:</span>
                        <span className="detail-value">{character.stats?.str || '0'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">DEX:</span>
                        <span className="detail-value">{character.stats?.dex || '0'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">INT:</span>
                        <span className="detail-value">{character.stats?.int || '0'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">LUK:</span>
                        <span className="detail-value">{character.stats?.luk || '0'}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <div className="dashboard-right">
          <div className="dashboard-section voting">
            <h2>Server Voting</h2>
            <div className="voting-content">
              <div className="gtop-logo-container">
                <GTop100Logo />
              </div>
              
              <div className="vote-info">
                <h3>Vote to Earn Rewards</h3>
                <p>Support our server by voting on GTop100 and receive <span className="nx-reward">{NX_REWARD_PER_VOTE} NX</span> per vote!</p>
                
                <div className="vote-progress">
                  <div className="progress-label">
                    <span>Today's Votes:</span>
                    <span className="progress-count">{userInfo.todayVotes} / 3</span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{width: `${(userInfo.todayVotes / 3) * 100}%`}}
                    ></div>
                  </div>
                </div>
                
                <div className="vote-stats">
                  <div className="vote-stat">
                    <span className="stat-value">{userInfo.votePoints}</span>
                    <span className="stat-label">Total Votes</span>
                  </div>
                  <div className="vote-stat">
                    <span className="stat-value">{NX_REWARD_PER_VOTE * userInfo.votePoints}</span>
                    <span className="stat-label">Total NX Earned</span>
                  </div>
                </div>
              </div>
              
              {voteMessage && (
                <div className={`vote-message ${voteMessage.type}`}>
                  {voteMessage.text}
                </div>
              )}
              
              <button 
                className={`vote-button ${voteLoading ? 'loading' : ''} ${!userInfo.canVoteToday ? 'disabled' : ''}`} 
                onClick={handleVote}
                disabled={voteLoading || !userInfo.canVoteToday}
              >
                {voteLoading ? 'Processing...' : userInfo.canVoteToday ? 'Vote Now for GTop100' : 'Daily Limit Reached'}
              </button>
              
              {!userInfo.canVoteToday && (
                <p className="vote-limit-message">
                  You've reached your daily voting limit (3/3). Come back tomorrow!
                </p>
              )}
            </div>
          </div>
          
          <div className="dashboard-section quick-links">
            <h2>Quick Links</h2>
            <div className="quick-links-grid">
              <a href="https://discord.gg/kuromaple" className="quick-link discord" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-discord"></i>
                <span>Join Discord</span>
              </a>
              <a href="https://download.kuromaple.com" className="quick-link download" target="_blank" rel="noopener noreferrer">
                <i className="fas fa-download"></i>
                <span>Download Client</span>
              </a>
              <a href="/rankings" className="quick-link rankings">
                <i className="fas fa-trophy"></i>
                <span>Rankings</span>
              </a>
              <a href="#" className="quick-link shop">
                <i className="fas fa-shopping-cart"></i>
                <span>Item Shop</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;